import { useRef, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { IconCheck, IconChevronDown, IconPlus, IconX } from "@tabler/icons-react";

import { Text } from "@fragments";
import Popper from "@fragments/Popper";

import "./styles.scss";

/**
 * ChipPicker — a filter chip that owns its own selection Popper.
 *
 * Three modes:
 *   1. Single-select:    pass `value` (id), `options`, `onChange(id, option)`
 *   2. Multi-select:     pass `multiple`, `value` (id[]), `options`, `onChange(ids, option)`
 *   3. Custom content:   pass `customContent={({ close }) => <ReactNode/>}`
 *                        — overrides the options list, useful for inline forms
 *
 * Each option: { id, label, optionContent?, searchLabel?, icon?, divider? }
 *   - `label` is shown both in the popper and as the pill in multi mode.
 *   - `optionContent` overrides the popper rendering when provided (e.g. JSX with badges).
 *   - `searchLabel` is the string used when `searchable` is on and `label` is JSX.
 *
 * @param {string}            title          bold header (contrast=9). Falls back to placeholder.
 * @param {string|ReactNode}  valueLabel     override the displayed value (single-select)
 * @param {string|number|Array} value        selected id, or array of ids when multiple
 * @param {Array}             options        select options
 * @param {boolean}           multiple       enable multi-select with pills + remove X
 * @param {function}          onChange       (newValue, option?) => void
 * @param {string}            placeholder    text when no value (defaults to the translated "Selecionar")
 * @param {boolean}           searchable     enable popper search input
 * @param {function}          customContent  ({ close }) => ReactNode — replaces options list
 * @param {function}          onClick        (event) => void — bypasses internal Popper, lets caller handle the click
 * @param {boolean}           activateOnHover open the picker on hover (closes when leaving the chip and the popper)
 * @param {string}            className      extra class names
 */
export default function ChipPicker({
  title,
  valueLabel,
  value,
  options = [],
  multiple = false,
  onChange,
  placeholder,
  searchable = false,
  customContent,
  onClick,
  activateOnHover = false,
  filled,
  className = "",
}) {
  // The placeholder default is resolved here, not in the parameter list, so it
  // follows the active language instead of freezing at module evaluation.
  const { t } = useTranslation("fragments");
  const resolvedPlaceholder = placeholder ?? t("common:actions.select");

  const popperRef = useRef(null);
  const valueRef = useRef(value);

  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  const selectedIds = multiple
    ? (Array.isArray(value) ? value : [])
    : value != null ? [value] : [];

  const selectedOptions = options.filter(o => !o.divider && selectedIds.includes(o.id));

  const hasItems = multiple && selectedOptions.length > 0;
  const isFilled = filled ?? (
    hasItems
    || (!multiple && selectedOptions.length > 0)
    || !!customContent
  );

  const { visibleItems, hiddenCount } = useMemo(() => {
    if (!hasItems) return { visibleItems: [], hiddenCount: 0 };

    const BUDGET = 40;
    let chars = 0;
    const visible = [];

    for (const opt of selectedOptions) {
      const labelStr = typeof opt.label === "string"
        ? opt.label
        : String(opt.label ?? "");
      const len = labelStr.length;

      // Always show at least one item, even if it exceeds the budget.
      if (visible.length === 0) {
        visible.push(opt);
        chars += len;
        continue;
      }

      if (chars + len > BUDGET) break;

      visible.push(opt);
      chars += len;
    }

    return {
      visibleItems: visible,
      hiddenCount: selectedOptions.length - visible.length,
    };
  }, [hasItems, selectedOptions]);

  const classes = [
    "chip-picker",
    isFilled && "chip-picker--filled",
    className,
  ].filter(Boolean).join(" ");

  const displayValue = !multiple
    ? (valueLabel ?? selectedOptions[0]?.label ?? resolvedPlaceholder)
    : null;

  function handleOpen(event) {
    if (onClick) {
      onClick(event);
      return;
    }

    if (customContent) {
      popperRef.current.open(
        [],
        "bottom-start",
        event.currentTarget,
        customContent({ close: () => popperRef.current.close() }),
      );
      return;
    }

    const popperOptions = options.map((opt, idx) => {
      if (opt.divider) return { id: `__div-${idx}`, divider: true };

      const isSelected = selectedIds.includes(opt.id);

      return {
        id: opt.id,
        label: opt.optionContent ?? opt.label,
        _searchLabel: opt.searchLabel ?? (typeof opt.label === "string" ? opt.label : ""),
        icon: opt.icon ?? (!multiple && isSelected ? <IconCheck size={14} /> : null),
        checked: multiple ? isSelected : undefined,
        disabled: opt.disabled,
        onClick: multiple
          ? () => commitMultipleToggle(opt)
          : () => commitSingle(opt),
      };
    });

    popperRef.current.open(popperOptions, "bottom-start", event.currentTarget);
  }

  function commitSingle(option) {
    valueRef.current = option.id;
    onChange?.(option.id, option);
    popperRef.current?.close();
  }

  function commitMultipleToggle(option) {
    const current = Array.isArray(valueRef.current) ? valueRef.current : [];
    const next = current.includes(option.id)
      ? current.filter(id => id !== option.id)
      : [...current, option.id];
    valueRef.current = next;
    onChange?.(next, option);
  }

  function handleRemoveItem(option) {
    const current = Array.isArray(valueRef.current) ? valueRef.current : [];
    const next = current.filter(id => id !== option.id);
    valueRef.current = next;
    onChange?.(next, option);
  }

  const popperConfig = {
    ...(multiple && { multiple: true }),
    ...(searchable && {
      searchKeyExtractor: (opt, validate) => validate(opt._searchLabel),
    }),
    ...(activateOnHover && { activateOnHover: true }),
  };

  return (
    <>
      <button
        type="button"
        className={classes}
        onClick={handleOpen}
        onMouseEnter={activateOnHover ? handleOpen : undefined}
      >
        {title && (
          <Text.span contrast={9} className="chip-picker__title">
            {title}
          </Text.span>
        )}

        {title && (hasItems || displayValue) && (
          <span className="chip-picker__separator" aria-hidden="true" />
        )}

        {hasItems ? (
          <span className="chip-picker__items">
            {visibleItems.map(opt => (
              <ChipPickerItem
                key={opt.id}
                option={opt}
                onRemove={handleRemoveItem}
              />
            ))}
            {hiddenCount > 0 && (
              <span className="chip-picker__more">
                <Text.span contrast={5}>
                  + e mais {hiddenCount} {hiddenCount === 1 ? "item" : "itens"}
                </Text.span>
              </span>
            )}
          </span>
        ) : displayValue ? (
          <Text.span contrast={6} className="chip-picker__value">
            {displayValue}
          </Text.span>
        ) : null}

        <span className="chip-picker__icon">
          {multiple ? (
            <IconPlus size={16} stroke={2} />
          ) : (
            <IconChevronDown size={16} stroke={2} />
          )}
        </span>
      </button>

      <Popper controlRef={popperRef} config={popperConfig}>
        <span />
      </Popper>
    </>
  );
}

function ChipPickerItem({ option, onRemove }) {
  const { t } = useTranslation("fragments");

  function handleRemove(e) {
    e.stopPropagation();
    onRemove(option);
  }

  return (
    <span className="chip-picker__item">
      <Text.span contrast={8}>{option.label}</Text.span>
      <span
        className="chip-picker__item-remove"
        role="button"
        tabIndex={0}
        aria-label={t("common:actions.remove")}
        onClick={handleRemove}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleRemove(e);
          }
        }}
      >
        <IconX size={12} stroke={2.5} />
      </span>
    </span>
  );
}
